import type { InclusionItem } from './inclusionItem';

export type CanonicalFeatureKey = 'processor' | 'screenSize' | 'ram' | 'hardDrive' | 'cellular' | 'warranty';

export interface CanonicalFeatureField {
  key: CanonicalFeatureKey;
  label: string;
  aliases: string[];
  unit?: string;
}

export const CANONICAL_FEATURE_FIELDS: CanonicalFeatureField[] = [
  {
    key: 'processor',
    label: 'Processor',
    aliases: ['processor', 'cpu', 'cpufamily', 'cpumodel', 'processormodel'],
  },
  {
    key: 'screenSize',
    label: 'Screen Size',
    aliases: ['screensize', 'screensizeinches', 'screen', 'display', 'displaysize', 'displayinches'],
    unit: 'inch',
  },
  {
    key: 'ram',
    label: 'RAM',
    aliases: ['ram', 'memory', 'memorygb', 'systemmemory', 'installedmemory'],
    unit: 'GB',
  },
  {
    key: 'hardDrive',
    label: 'Hard Drive',
    aliases: ['harddrive', 'storage', 'storagegb', 'drive', 'ssd', 'hdd', 'internalstorage'],
    unit: 'GB',
  },
  {
    key: 'cellular',
    label: 'Cellular',
    aliases: ['cellular', 'wwan', 'mobilebroadband', 'lte', '5g', '4g', 'cellularconnectivity', 'wirelesswan'],
  },
  {
    key: 'warranty',
    label: 'Warranty',
    aliases: ['warranty', 'warrantyyears', 'manufacturerwarranty', 'support', 'onsitewarranty'],
    unit: 'year',
  },
];

const FIELD_BY_KEY = new Map(CANONICAL_FEATURE_FIELDS.map((field) => [field.key, field]));
const FIELD_BY_ALIAS = new Map<string, CanonicalFeatureField>();

for (const field of CANONICAL_FEATURE_FIELDS) {
  FIELD_BY_ALIAS.set(normaliseToken(field.key), field);
  FIELD_BY_ALIAS.set(normaliseToken(field.label), field);
  field.aliases.forEach((alias) => FIELD_BY_ALIAS.set(normaliseToken(alias), field));
}

const EMPTY_VALUES = new Set(['', 'n/a', 'na', 'none', 'null', 'unknown', '-', '--']);

export function normaliseFeatureAttributes(attributes: Record<string, unknown> | undefined): Record<CanonicalFeatureKey, string> {
  const next: Partial<Record<CanonicalFeatureKey, string>> = {};
  if (!attributes) return next as Record<CanonicalFeatureKey, string>;

  for (const [rawKey, rawValue] of Object.entries(attributes)) {
    const field = FIELD_BY_ALIAS.get(normaliseToken(rawKey));
    if (!field || next[field.key]) continue;
    const formatted = formatFeatureValue(rawValue, field);
    if (formatted) next[field.key] = formatted;
  }

  return next as Record<CanonicalFeatureKey, string>;
}

export function featureListToAttributes(features: string[] | InclusionItem[] | undefined): Record<CanonicalFeatureKey, string> {
  const next: Partial<Record<CanonicalFeatureKey, string>> = {};
  for (const item of features ?? []) {
    const text = typeof item === 'string' ? item : item?.text;
    if (!text) continue;
    const separator = text.indexOf(':');
    if (separator <= 0) continue;
    const label = text.slice(0, separator).trim();
    const value = text.slice(separator + 1).trim();
    const field = FIELD_BY_ALIAS.get(normaliseToken(label));
    if (!field || next[field.key]) continue;
    const formatted = formatFeatureValue(value, field);
    if (formatted) next[field.key] = formatted;
  }
  return next as Record<CanonicalFeatureKey, string>;
}

export function canonicalFeatureEditRows(attributes: Record<string, unknown> | undefined): Array<{ key: CanonicalFeatureKey; label: string; value: string }> {
  const normalised = normaliseFeatureAttributes(attributes);
  return CANONICAL_FEATURE_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    value: normalised[field.key] ?? '',
  }));
}

export function editableRowsToFeatureAttributes(rows: Array<{ key: CanonicalFeatureKey; value: string }>): Record<CanonicalFeatureKey, string> {
  const next: Partial<Record<CanonicalFeatureKey, string>> = {};
  rows.forEach((row) => {
    const field = FIELD_BY_KEY.get(row.key);
    if (!field) return;
    const formatted = formatFeatureValue(row.value, field);
    if (formatted) next[row.key] = formatted;
  });
  return next as Record<CanonicalFeatureKey, string>;
}

export function attributesToFeatureRows(attributes: Record<string, unknown> | undefined): Array<{ text: string; icon?: string }> {
  const normalised = normaliseFeatureAttributes(attributes);
  return CANONICAL_FEATURE_FIELDS
    .map((field) => {
      const value = normalised[field.key];
      return value ? { text: `${field.label}: ${value}` } : null;
    })
    .filter((row): row is { text: string; icon?: string } => row !== null);
}

export function attributesToFeatureStrings(attributes: Record<string, unknown> | undefined): string[] {
  return attributesToFeatureRows(attributes).map((row) => row.text);
}

function normaliseToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function formatFeatureValue(value: unknown, field: CanonicalFeatureField): string {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map((part) => formatFeatureValue(part, field)).filter(Boolean).join(', ');
  }

  let text = typeof value === 'string'
    ? value.trim()
    : typeof value === 'boolean'
      ? (value ? 'Yes' : 'No')
      : typeof value === 'number'
      ? String(value)
      : safeStringify(value);

  if (!text || EMPTY_VALUES.has(text.toLowerCase())) return '';
  if (field.unit) text = appendUnitIfMissing(text, field.unit);
  return text;
}

function appendUnitIfMissing(value: string, unit: string): string {
  if (!/^\d+(?:\.\d+)?$/.test(value.trim())) return value;
  const trimmed = value.trim();
  const suffix = unit === 'year' && trimmed !== '1' ? 'years' : unit;
  return `${trimmed} ${suffix}`;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
